import type { ReactNode } from "react"
import { useId, useMemo } from "react"
import * as R from "remeda"

import type {
	AugmentedIssue,
	AugmentedProject,
	ProjectsOrIssues,
} from "@/lib/project-or-issue-helpers"
import { ImportanceTooltip } from "@/components/issue-or-project-items/importance-tooltip"
import { ProjectOrIssueListItem } from "@/components/issue-or-project-items/project-or-issue-list-item"
import {
	SortableListItem,
	SortableRows,
} from "@/components/issue-or-project-items/sortable-list-item"
import { TrendTooltip } from "@/components/issue-or-project-items/trend-tooltip"
import { groupProjectsByLinearRoadmaps } from "@/lib/project-or-issue-helpers"
import { useZeroWorkspace } from "@/lib/zero"
import { PendingPortalItem } from "@/screens/app/roadmap/pending-portal-item"

type Props = {
	status: string
	maxMap: Map<string, number> | null
	/** Render items with X/check (publish-or-hide) controls instead of the visibility switch. */
	isPending?: boolean
	/** Render a custom trailing control per row instead of the visibility switch. */
	renderTrailingAction?: (
		type: "project" | "issue",
		data: { id: string },
	) => ReactNode
	/** Turns on drag to reorder; gets the initiative id for project lists. */
	sortableListId?: (roadmapId: string | null) => string
} & ProjectsOrIssues

export function ProjectOrIssueListSection({
	type,
	data,
	status,
	maxMap,
	isPending: isPendingProp,
	renderTrailingAction,
	sortableListId,
}: Props) {
	const workspace = useZeroWorkspace()

	const isPending = isPendingProp ?? status === "Pending"

	const sortedProjects = useMemo(() => {
		if (type !== "project") return []
		if (isPending) {
			return R.sortBy(data, [(item) => item.totalScore, "desc"])
		}
		return data
	}, [data, isPending, type])

	const sortedIssues = useMemo(() => {
		if (type !== "issue") return []
		if (isPending) {
			return R.sortBy(data, [(item) => item.totalScore, "desc"])
		}
		return data
	}, [data, isPending, type])

	const { projectsWithRoadmaps, projectsWithoutRoadmaps } = useMemo(() => {
		if (type === "project") {
			return groupProjectsByLinearRoadmaps(
				sortedProjects,
				workspace?.isRoadmapsEnabled ?? false,
			)
		} else {
			return {
				projectsWithRoadmaps: [],
				projectsWithoutRoadmaps: [],
			}
		}
	}, [sortedProjects, type, workspace?.isRoadmapsEnabled])

	const pendingMaxPainLevel = useMemo(() => {
		if (!isPending) return 0
		return data.reduce((max, item) => Math.max(max, item.totalScore), 0)
	}, [data, isPending])

	const maxMapValue: Map<string, number> = useMemo(() => {
		if (!maxMap) {
			const stateOrStatusMaxScores = data.reduce(
				(acc, item) => {
					const key =
						type === "project"
							? (item as AugmentedProject).state
							: (item as AugmentedIssue).status
					const score = item.totalScore

					if (!key) return acc

					if (!acc[key] || score > acc[key]) {
						acc[key] = score
					}

					return acc
				},
				{} as Record<string, number>,
			)

			return new Map(Object.entries(stateOrStatusMaxScores))
		}
		return maxMap
	}, [maxMap, data, type])

	const id = useId()

	const listIdFor = (roadmapId: string | null) =>
		sortableListId && !isPending ? sortableListId(roadmapId) : null

	const renderProject = (project: AugmentedProject, listId: string | null) => {
		if (isPending) {
			return (
				<PendingPortalItem
					key={project.id}
					data={project}
					type="project"
					maxPainLevel={pendingMaxPainLevel}
				/>
			)
		}
		const row = (dragHandle?: ReactNode) => (
			<ProjectOrIssueListItem
				key={project.id}
				data={project}
				type="project"
				hidePriority
				maxPainLevel={maxMapValue.get(project.state ?? "") ?? 0}
				trailingAction={renderTrailingAction?.("project", project)}
				dragHandle={dragHandle}
			/>
		)
		if (!listId) return row()
		return (
			<SortableListItem
				key={project.id}
				listId={listId}
				rowType="project"
				itemId={project.id}
			>
				{row}
			</SortableListItem>
		)
	}

	const renderIssue = (
		issue: (typeof sortedIssues)[number],
		listId: string | null,
	) => {
		if (isPending) {
			return (
				<PendingPortalItem
					key={issue.id}
					data={issue}
					type="issue"
					maxPainLevel={pendingMaxPainLevel}
				/>
			)
		}
		const row = (dragHandle?: ReactNode) => (
			<ProjectOrIssueListItem
				key={issue.id}
				type="issue"
				data={issue}
				maxPainLevel={maxMapValue.get(issue.status) ?? 0}
				trailingAction={renderTrailingAction?.("issue", issue)}
				dragHandle={dragHandle}
			/>
		)
		if (!listId) return row()
		return (
			<SortableListItem
				key={issue.id}
				listId={listId}
				rowType="issue"
				itemId={issue.id}
			>
				{row}
			</SortableListItem>
		)
	}

	if (!data.length) return null
	return (
		<div>
			<div className="hidden items-center gap-4 px-6 py-2 text-xs text-secondary">
				<div className="flex-1">{type === "project" ? "Project" : "Issue"}</div>
				<div className="flex w-24 items-center gap-2">
					<div>Priority</div>
					<ImportanceTooltip />
				</div>
				<div className="flex w-14 items-center gap-2">
					<div>Trend</div>
					<TrendTooltip />
				</div>
				<div hidden={!workspace?.domainPublic} className="w-12">
					Visible
				</div>
			</div>

			<SortableRows
				listId={listIdFor(null)}
				itemIds={projectsWithoutRoadmaps.map((project) => project.id)}
			>
				{projectsWithoutRoadmaps.map((project) =>
					renderProject(project, listIdFor(null)),
				)}
			</SortableRows>

			{projectsWithRoadmaps.map((roadmap) => {
				return (
					<div key={`${roadmap.id}-${id}`}>
						<div className="mt-2 flex items-center px-6 py-1.5 text-xs font-medium">
							<div>{roadmap.name}</div>
							<div className="flex flex-1 items-center justify-end">
								<div className="ml-2 flex-1 border-t border-gray-300 dark:border-gray-700/50"></div>
							</div>
						</div>
						<SortableRows
							listId={listIdFor(roadmap.id)}
							itemIds={roadmap.projects.map((project) => project.id)}
						>
							{roadmap.projects.map((project) =>
								renderProject(project, listIdFor(roadmap.id)),
							)}
						</SortableRows>
					</div>
				)
			})}

			{type === "issue" ? (
				<SortableRows
					listId={listIdFor(null)}
					itemIds={sortedIssues.map((issue) => issue.id)}
				>
					{sortedIssues.map((issue) => renderIssue(issue, listIdFor(null)))}
				</SortableRows>
			) : null}
		</div>
	)
}
